/**
 * Accessibility Test Page
 * Comprehensive testing component for all accessibility features
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AccessibleMotion } from '../lib/animations/AccessibleMotion';
import KeyboardNavigation from './KeyboardNavigation';
import TouchOptimizedInteraction from './TouchOptimizedInteraction';
import { useAccessibility, useResponsiveAnimation, usePerformanceOptimization } from '../lib/animations';

const AccessibilityTestPage: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [currentTest, setCurrentTest] = useState<string>('');
  
  const { announce, focusElement } = useAccessibility({
    announceChanges: true,
    enableKeyboardNavigation: true
  });
  
  const { deviceCapabilities } = useResponsiveAnimation({});
  const { performanceState, metrics } = usePerformanceOptimization();

  const addTestResult = (result: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${result}`]);
    announce(result);
  };

  const runAccessibilityTests = () => {
    setCurrentTest('Running accessibility tests...');
    
    // Test 1: Reduced motion detection
    const reducedMotionSupported = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    addTestResult(`Reduced motion detection: ${reducedMotionSupported ? 'PASS' : 'PASS (not preferred)'}`);
    
    // Test 2: Keyboard navigation
    const focusableElements = document.querySelectorAll('[tabindex], button, input, a[href]');
    addTestResult(`Focusable elements found: ${focusableElements.length}`);
    
    // Test 3: ARIA labels
    const ariaLabels = document.querySelectorAll('[aria-label]');
    addTestResult(`ARIA labels found: ${ariaLabels.length}`);
    
    // Test 4: Screen reader announcements
    announce('Screen reader announcement test - this should be announced');
    addTestResult('Screen reader announcement test completed');
    
    // Test 5: Device capabilities
    addTestResult(`Device: ${deviceCapabilities.isMobile ? 'Mobile' : deviceCapabilities.isTablet ? 'Tablet' : 'Desktop'}`);
    addTestResult(`Touch device: ${deviceCapabilities.isTouchDevice ? 'Yes' : 'No'}`);
    
    setCurrentTest('');
  };

  const runPerformanceTests = () => {
    setCurrentTest('Running performance tests...');
    
    addTestResult(`Current FPS: ${metrics.fps}`);
    addTestResult(`Memory usage: ${metrics.memoryUsage.toFixed(1)}MB`);
    addTestResult(`Active animations: ${metrics.activeAnimations}`);
    addTestResult(`Performance level: ${performanceState.currentLevel}`);
    addTestResult(`Performance violations: ${performanceState.violations}`);
    
    setCurrentTest('');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <AccessibleMotion
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          ariaLabel="Accessibility Test Page"
          announceChanges={true}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Accessibility & Performance Test Suite
          </h1>
        </AccessibleMotion>

        <KeyboardNavigation
          enabled={true}
          trapFocus={false}
          announceNavigation={true}
        >
          {/* Test Controls */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Test Controls</h2>
            
            <div className="flex flex-wrap gap-4 mb-4">
              <button
                onClick={runAccessibilityTests}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Run accessibility tests"
              >
                Run Accessibility Tests
              </button>
              
              <button
                onClick={runPerformanceTests}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                aria-label="Run performance tests"
              >
                Run Performance Tests
              </button>
              
              <button
                onClick={() => setTestResults([])}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label="Clear test results"
              >
                Clear Results
              </button>
            </div>
            
            {currentTest && (
              <div className="text-blue-600 font-medium" aria-live="polite">
                {currentTest}
              </div>
            )}
          </div>

          {/* Interactive Elements Test */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Interactive Elements</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Accessible Motion Test */}
              <AccessibleMotion
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-100 p-4 rounded-lg cursor-pointer"
                ariaLabel="Accessible motion test button"
                focusable={true}
                onClick={() => addTestResult('Accessible motion element clicked')}
              >
                <h3 className="font-medium">Accessible Motion</h3>
                <p className="text-sm text-gray-600">Hover and click to test</p>
              </AccessibleMotion>

              {/* Touch Optimized Test */}
              <TouchOptimizedInteraction
                onTap={() => addTestResult('Touch optimized tap detected')}
                onSwipeLeft={() => addTestResult('Swipe left detected')}
                onSwipeRight={() => addTestResult('Swipe right detected')}
                onLongPress={() => addTestResult('Long press detected')}
                accessibilityLabel="Touch interaction test"
                className="bg-green-100 p-4 rounded-lg"
              >
                <h3 className="font-medium">Touch Optimized</h3>
                <p className="text-sm text-gray-600">Tap, swipe, or long press</p>
              </TouchOptimizedInteraction>

              {/* Keyboard Navigation Test */}
              <div className="bg-purple-100 p-4 rounded-lg">
                <h3 className="font-medium">Keyboard Navigation</h3>
                <div className="space-y-2 mt-2">
                  <button
                    className="block w-full text-left px-2 py-1 bg-white rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    onClick={() => addTestResult('Keyboard nav button 1 clicked')}
                  >
                    Button 1
                  </button>
                  <button
                    className="block w-full text-left px-2 py-1 bg-white rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    onClick={() => addTestResult('Keyboard nav button 2 clicked')}
                  >
                    Button 2
                  </button>
                  <button
                    className="block w-full text-left px-2 py-1 bg-white rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    onClick={() => addTestResult('Keyboard nav button 3 clicked')}
                  >
                    Button 3
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Device Information */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Device Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium mb-2">Screen Size</h3>
                <ul className="text-sm space-y-1">
                  <li>Mobile: {deviceCapabilities.isMobile ? '✓' : '✗'}</li>
                  <li>Tablet: {deviceCapabilities.isTablet ? '✓' : '✗'}</li>
                  <li>Desktop: {deviceCapabilities.isDesktop ? '✓' : '✗'}</li>
                  <li>Wide: {deviceCapabilities.isWide ? '✓' : '✗'}</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Capabilities</h3>
                <ul className="text-sm space-y-1">
                  <li>Touch Device: {deviceCapabilities.isTouchDevice ? '✓' : '✗'}</li>
                  <li>Low-end Device: {deviceCapabilities.isLowEndDevice ? '✓' : '✗'}</li>
                  <li>Connection: {deviceCapabilities.connectionSpeed}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Performance Metrics</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{metrics.fps}</div>
                <div className="text-sm text-gray-600">FPS</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {metrics.memoryUsage.toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">Memory (MB)</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {metrics.activeAnimations}
                </div>
                <div className="text-sm text-gray-600">Active Animations</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {performanceState.violations}
                </div>
                <div className="text-sm text-gray-600">Violations</div>
              </div>
            </div>
          </div>

          {/* Test Results */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Test Results</h2>
            
            <div 
              className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto"
              aria-live="polite"
              aria-label="Test results log"
            >
              {testResults.length === 0 ? (
                <p className="text-gray-500 italic">No test results yet. Run some tests to see results here.</p>
              ) : (
                <ul className="space-y-1">
                  {testResults.map((result, index) => (
                    <li key={index} className="text-sm font-mono">
                      {result}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </KeyboardNavigation>
      </div>
    </div>
  );
};

export default AccessibilityTestPage;